import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';

import Autocomplete from './Autocomplete';
import DatePicker from './DatePicker';
import { t } from 'i18next';

const Input = ({
  value,
  type = 'text',

  name = '',
  className = '',
  placeholder = '',
  label = '',
  helper = '',
  invalid = '',

  options = [],
  disabled = false,
  readOnly = false,

  dateFormat = t('dateFormat'),
  datePickerHelper,
  datePickerMin,
  datePickerMax,
  datePickerMaxYears,

  onChange,
  onSelect,
  onBlur
}) => {
  let [isCheckbox, setIsCheckbox] = useState(false);
  let [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const handleChange = useCallback((e, callback) => {
    if (type === 'date') {
      setIsDatePickerOpen(false);
      onChange(name, e);
    } else if (type === 'checkbox' || type === 'toggle') {
      onChange(name, e.target.checked);
    } else if (type === 'autocomplete') {
      onChange(name, e.target.value, callback);
    } else if (onChange) {
      onChange(name, e.target.value);
    }
  }, [type, name, onChange]);

  const handleBlur = useCallback(() => {
    onBlur && onBlur(name);
  }, [name, onBlur]);

  useEffect(() => {
    setIsCheckbox(['checkbox', 'toggle'].includes(type));
  }, [type]);

  let inputElement;
  const inputProps = {
    className: 'input__field',
    value: value || '',
    disabled,
    readOnly,
    onChange: handleChange,
    onBlur: handleBlur
  };

  switch (type) {
    case 'autocomplete':
      inputElement = (
        <Autocomplete
          {...inputProps}
          placeholder={placeholder}
          onSelect={onSelect}
        />
      );
      break;
    case 'checkbox':
      inputElement = (
        <label className={inputProps.className}>
          <input {...inputProps} className='' type='checkbox' checked={value} />
          <span className='input__checkbox'></span>
          {label}
        </label>
      );
      break;
    case 'toggle':
      inputElement = (
        <label className={inputProps.className}>
          {label}
          <input {...inputProps} className='' type='checkbox' checked={value} />
          <span className='input__toggle'></span>
        </label>
      );
      break;
    case 'select':
      inputElement = (
        <select {...inputProps}>
          {options.map((option, i) => (
            <option key={i} value={option.value || option}>
              {option.hasOwnProperty('text') ? option.text : option}
            </option>
          ))}
        </select>
      );
      break;
    case 'textarea':
      inputElement = (
        <textarea {...inputProps} placeholder={placeholder} />
      );
      break;
    case 'date':
      inputElement = (
        <input
          {...inputProps}
          type='text'
          value={value ? dayjs(value).format(dateFormat) : ''}
          placeholder={placeholder}
          readOnly={true}
          onClick={() => setIsDatePickerOpen(true)}
          onFocus={() => setIsDatePickerOpen(true)}
        />
      );
      break;
    default:
      inputElement = (
        <input
          {...inputProps}
          type={type}
          placeholder={placeholder}
        />
      );
  }

  return (
    <div className={[
      'input',
      type && `input--${type}`,
      className && `input--${className}`,
      invalid && 'input--invalid',
      disabled && 'input--disabled'
    ].filter(Boolean).join(' ')}>
      {isCheckbox && inputElement}
      {!isCheckbox && (<>
        {label && <label className='input__label'>{label}</label>}
        <div className='input__container'>{inputElement}</div>
      </>)}
      {helper && <p className='input__helper'>{helper}</p>}
      {invalid && <p className='input__invalid'>{invalid}</p>}
      {isDatePickerOpen && (<DatePicker
        value={value ? dayjs(value).toDate() : ''}
        label={datePickerHelper}
        minDate={datePickerMin}
        maxDate={datePickerMax}
        maxYears={datePickerMaxYears}
        onChange={handleChange}
        onClickOutside={() => setIsDatePickerOpen(false)}
      />)}
    </div>
  );
};

export default Input;
